import { useState, useEffect, useCallback } from "react";
import {
  retryFailedPaymentApi,
  markPaymentAsResolvedApi,
  processRefundApi,
  markPaymentAsClosedApi,
  getFailedPaymentsApi,
} from "../../../utils/api/failedPayments";
import Notiflix from "notiflix";
import { useIntl } from "react-intl";

export function useFailedPaymentsManagement() {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [failedPayments, setFailedPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  const fetchFailedPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFailedPaymentsApi({ page: 1, limit: 100 });
      setFailedPayments(response.data.payments);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch failed payments:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "admin.failedPayments.refreshError",
          defaultMessage: "Failed to refresh failed payments",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [intl]);

  useEffect(() => {
    // Load failed payments on component mount
    fetchFailedPayments();
  }, [fetchFailedPayments]);

  const refreshFailedPayments = async () => {
    setLoading(true);
    try {
      const response = await getFailedPaymentsApi({ page: 1, limit: 100 });
      setFailedPayments(response.data.payments);
      setTotal(response.data.total);

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "admin.failedPayments.refreshSuccess",
          defaultMessage: "Failed payments refreshed successfully",
        })
      );
    } catch (error) {
      console.error("Failed to refresh failed payments:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "admin.failedPayments.refreshError" })
      );
    } finally {
      setLoading(false);
    }
  };

  const retryPayment = async (paymentId: string) => {
    setRetryLoading(true);
    try {
      await retryFailedPaymentApi(paymentId);

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "admin.failedPayments.retrySuccess",
          defaultMessage: "Payment retry initiated successfully",
        })
      );

      // Refresh the failed payments to get updated status
      await fetchFailedPayments();
    } catch (error) {
      console.error("Failed to retry payment:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "admin.failedPayments.retryError",
          defaultMessage: "Failed to retry payment",
        })
      );
    } finally {
      setRetryLoading(false);
    }
  };

  const markAsResolved = async (paymentId: string, notes: string) => {
    try {
      await markPaymentAsResolvedApi(paymentId, notes);

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "admin.failedPayments.resolveSuccess",
          defaultMessage: "Payment marked as resolved",
        })
      );

      // Refresh the failed payments
      await fetchFailedPayments();
    } catch (error) {
      console.error("Failed to mark payment as resolved:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "admin.failedPayments.resolveError",
          defaultMessage: "Failed to resolve payment",
        })
      );
    }
  };

  const processRefund = async (paymentId: string, reason: string) => {
    try {
      await processRefundApi(paymentId, reason);

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "admin.failedPayments.refundSuccess",
          defaultMessage: "Refund processed successfully",
        })
      );

      // Refresh the failed payments
      await fetchFailedPayments();
    } catch (error) {
      console.error("Failed to process refund:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "admin.failedPayments.refundError",
          defaultMessage: "Failed to process refund",
        })
      );
    }
  };

  const closePayment = async (paymentId: string) => {
    try {
      await markPaymentAsClosedApi(paymentId);

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "admin.failedPayments.closeSuccess",
          defaultMessage: "Payment closed successfully",
        })
      );

      // Refresh the failed payments
      await fetchFailedPayments();
    } catch (error) {
      console.error("Failed to close payment:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "admin.failedPayments.closeError",
          defaultMessage: "Failed to close payment",
        })
      );
    }
  };

  return {
    payments: failedPayments,
    loading,
    retryLoading,
    total,
    refreshFailedPayments,
    retryPayment,
    markAsResolved,
    processRefund,
    closePayment,
  };
}
